import React, { useCallback, useState, useEffect } from 'react';
import './MarcasMonitoradas.css';
import { apiFetch, ApiError } from './api';
import { useRecurso } from './hooks/useRecurso';
import EstadoCarregando from './EstadoCarregando';
import EstadoErro from './EstadoErro';
import EstadoVazio from './EstadoVazio';

// Mensagens pt-BR por código de erro do backend (contrato { error: { code } }).
const MENSAGENS_ERRO = {
  FORBIDDEN: 'Você não tem permissão para ver as marcas monitoradas.',
  NOT_FOUND: 'Lista de marcas monitoradas não encontrada.',
  RATE_LIMITED: 'Muitas requisições. Aguarde um momento e tente novamente.',
};

function mensagemErro(erro) {
  if (erro instanceof ApiError) {
    if (erro.status === 429) return MENSAGENS_ERRO.RATE_LIMITED;
    if (erro.code && MENSAGENS_ERRO[erro.code]) {
      return MENSAGENS_ERRO[erro.code];
    }
    return 'Não foi possível carregar as marcas monitoradas.';
  }
  return 'Erro de conexão. Verifique sua internet e tente novamente.';
}

// Datas do backend chegam ISO (LocalDate "2024-01-15" / LocalDateTime).
// Exibição do produto é DD/MM/YYYY (convenção CLAUDE.md). LocalDate (10 chars)
// é tratado como meia-noite local para evitar off-by-one em fusos negativos.
function formatarData(valor) {
  if (!valor) return '—';
  const iso = String(valor).length === 10 ? `${valor}T00:00:00` : valor;
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '—';
  return data.toLocaleDateString('pt-BR');
}

const ITENS_POR_PAGINA = 20;

function MarcasMonitoradas() {
  // Listagem real — GET /api/marcas/monitoradas (PRD 11 / SEF-129) com filtros
  // (q/status/classe) e paginação (limit/offset) server-side (SEF-143). O
  // contrato é { data: [MonitoramentoResponse], metadata: PageMetadata }.
  const [filtros, setFiltros] = useState({ q: '', status: '', classe: '' });
  const [qBusca, setQBusca] = useState(''); // q com debounce, usada na query
  const [pagina, setPagina] = useState(1);

  // Debounce do campo de busca. O reset de página acontece no MESMO evento que
  // aplica a busca (React 18 faz batch no timeout → 1 render → 1 fetch), evitando
  // uma chamada intermediária com filtro novo + offset antigo (review do CTO).
  useEffect(() => {
    const t = setTimeout(() => {
      setQBusca(filtros.q.trim());
      setPagina(1);
    }, 400);
    return () => clearTimeout(t);
  }, [filtros.q]);

  const buscar = useCallback(() => {
    const params = new URLSearchParams();
    if (qBusca) params.set('q', qBusca);
    if (filtros.status.trim()) params.set('status', filtros.status.trim());
    if (filtros.classe.trim()) params.set('classe', filtros.classe.trim());
    params.set('limit', String(ITENS_POR_PAGINA));
    params.set('offset', String((pagina - 1) * ITENS_POR_PAGINA));
    return apiFetch(`/api/marcas/monitoradas?${params.toString()}`);
  }, [qBusca, filtros.status, filtros.classe, pagina]);

  const { dados, carregando, erro, recarregar } = useRecurso(buscar, [
    qBusca,
    filtros.status,
    filtros.classe,
    pagina,
  ]);
  const marcas = dados?.data ?? [];
  const meta = dados?.metadata;
  const totalPaginas = meta?.totalPages ?? 1;
  const temFiltro = Boolean(qBusca || filtros.status.trim() || filtros.classe.trim());

  // status/classe aplicam na hora e resetam a página no mesmo evento; q reseta
  // no debounce acima. Assim nunca há busca com filtro novo + offset antigo.
  const mudarFiltro = (campo, valor) => {
    setFiltros((f) => ({ ...f, [campo]: valor }));
    if (campo !== 'q') setPagina(1);
  };

  const limparFiltros = () => {
    setFiltros({ q: '', status: '', classe: '' });
    setQBusca(''); // zera a busca aplicada na hora; não espera o debounce
    setPagina(1);
  };

  return (
    <div className="marcas-monitoradas-container">
      <div className="marcas-monitoradas-header">
        <h1>Marcas Monitoradas</h1>
        <p className="descricao">
          Marcas que o escritório acompanha junto ao INPI
        </p>
      </div>

      <div className="monitoradas-filtros">
        <input
          type="search"
          className="filtro-busca"
          placeholder="Buscar por marca ou processo"
          aria-label="Buscar"
          value={filtros.q}
          onChange={(e) => mudarFiltro('q', e.target.value)}
        />
        <input
          type="text"
          className="filtro-status"
          placeholder="Status"
          aria-label="Filtrar por status"
          value={filtros.status}
          onChange={(e) => mudarFiltro('status', e.target.value)}
        />
        <input
          type="text"
          className="filtro-classe"
          placeholder="Classe"
          aria-label="Filtrar por classe"
          value={filtros.classe}
          onChange={(e) => mudarFiltro('classe', e.target.value)}
        />
        {temFiltro && (
          <button type="button" className="btn-limpar-filtros" onClick={limparFiltros}>
            Limpar filtros
          </button>
        )}
      </div>

      {carregando ? (
        <EstadoCarregando mensagem="Carregando marcas monitoradas..." />
      ) : erro ? (
        <EstadoErro mensagem={mensagemErro(erro)} onRetry={recarregar} />
      ) : marcas.length === 0 ? (
        temFiltro ? (
          <EstadoVazio
            icone="fa-solid fa-filter-circle-xmark"
            titulo="Nenhum resultado"
            descricao="Nenhuma marca monitorada corresponde aos filtros aplicados."
            acao={{ rotulo: 'Limpar filtros', onClick: limparFiltros }}
          />
        ) : (
          <EstadoVazio
            icone="fa-solid fa-binoculars"
            titulo="Comece a monitorar marcas"
            descricao="As marcas que o seu escritório acompanha aparecem aqui. Verifique uma marca para receber alertas de publicações no INPI."
            acao={{ rotulo: 'Verificar uma marca', onClick: () => window.onNavegar && window.onNavegar('marcas') }}
          />
        )
      ) : (
        <div className="tabela-container">
          <table className="tabela-monitoradas">
            <thead>
              <tr>
                <th>Marca</th>
                <th>Processo</th>
                <th>Classe</th>
                <th>Cliente</th>
                <th>Status</th>
                <th>Data Depósito</th>
                <th>Última Atualização</th>
              </tr>
            </thead>
            <tbody>
              {marcas.map((marca) => (
                <tr key={marca.id}>
                  <td><strong>{marca.nome}</strong></td>
                  <td>{marca.processo || '—'}</td>
                  <td>{marca.classe || '—'}</td>
                  <td>{marca.clienteNome || '—'}</td>
                  <td>
                    {marca.status ? (
                      <span className={`status ${String(marca.status).toLowerCase().replace(/ /g, '-')}`}>
                        {marca.status}
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{formatarData(marca.dataDeposito)}</td>
                  <td>{formatarData(marca.ultimaAtualizacao)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!carregando && !erro && marcas.length > 0 && totalPaginas > 1 && (
        <div className="monitoradas-paginacao">
          <button
            type="button"
            onClick={() => setPagina((p) => Math.max(1, p - 1))}
            disabled={pagina <= 1}
          >
            Anterior
          </button>
          <span className="paginacao-info">
            Página {pagina} de {totalPaginas}
          </span>
          <button
            type="button"
            onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
            disabled={pagina >= totalPaginas}
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
}

export default MarcasMonitoradas;
